import React, {type RefObject, useCallback, useRef} from 'react';

import {INPUT_ACTION, useRegisterAction} from '../../../inputSystem';
import {useRampFlagMediaCompareSyncZoomUser} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useStablePanelId} from '../../Panel/PanelIDContext';
import {
  type Zoom,
  type ZoomAction,
  ZoomControl,
  zoomIn,
  zoomOut,
} from '../controls/ZoomControl';
import {selectViewport, useCompareViewportStore} from './compareViewportStore';
import {
  type CompareViewportView,
  useCompareZoomActivation,
} from './useCompareZoomActivation';

function zoomForAction(action: ZoomAction, zoom: Zoom): Zoom {
  if (action === 'zoom_in') {
    return zoomIn(zoom, null);
  }
  if (action === 'zoom_out') {
    return zoomOut(zoom, null);
  }
  if (action === 'zoom_100') {
    return 100;
  }
  return 'fit';
}

function changeZoomFromKeyboard(
  panelId: string,
  activeOwnerRef: RefObject<string | null>,
  action: ZoomAction
) {
  const state = useCompareViewportStore.getState();
  const {zoom, isSynced} = selectViewport(panelId)(state);
  if (
    activeOwnerRef.current == null ||
    state.activePanelOwnerId !== activeOwnerRef.current ||
    state.activePanelId !== panelId ||
    !isSynced
  ) {
    return;
  }
  // Compare leaves Fit only through the explicit 100% action.
  if (zoom === 'fit' && (action === 'zoom_in' || action === 'zoom_out')) {
    return;
  }
  state.setZoom(panelId, zoomForAction(action, zoom));
}

type ShortcutProps = {
  panelId: string;
  activeOwnerRef: RefObject<string | null>;
};

function CompareZoomShortcuts({panelId, activeOwnerRef}: ShortcutProps) {
  const onZoomIn = useCallback(() => {
    changeZoomFromKeyboard(panelId, activeOwnerRef, 'zoom_in');
  }, [panelId, activeOwnerRef]);
  useRegisterAction(INPUT_ACTION.MEDIA_COMPARE_ZOOM_IN, onZoomIn);
  useRegisterAction(INPUT_ACTION.MEDIA_COMPARE_ZOOM_IN_SHIFTED, onZoomIn);
  useRegisterAction(INPUT_ACTION.MEDIA_COMPARE_ZOOM_OUT, () => {
    changeZoomFromKeyboard(panelId, activeOwnerRef, 'zoom_out');
  });
  useRegisterAction(INPUT_ACTION.MEDIA_COMPARE_RESET_ZOOM, () => {
    changeZoomFromKeyboard(panelId, activeOwnerRef, 'zoom_100');
  });
  useRegisterAction(INPUT_ACTION.MEDIA_COMPARE_ZOOM_TO_FIT, () => {
    changeZoomFromKeyboard(panelId, activeOwnerRef, 'zoom_fit');
  });
  return null;
}

type Props = {
  view: CompareViewportView;
  onMenuOpenChange?: (isOpen: boolean) => void;
};

/**
 * The Compare panel header's zoom control, wired to the shared viewport
 * store. Mounted in shared headers only for media panels; eligibility comes
 * from the panel lifecycle's published state, so it renders nothing until a
 * media panel is in Compare mode and disables itself while fan-out keeps the
 * panel unsynced. Leaving Fit starts at 100% before shared numeric zoom steps.
 * The last-activated panel owns the zoom keyboard shortcuts.
 */
export function CompareZoomControl({view, onMenuOpenChange}: Props) {
  const compareZoomEnabled = useRampFlagMediaCompareSyncZoomUser() === true;
  const panelId = useStablePanelId();
  const zoom = useCompareViewportStore(
    state => selectViewport(panelId)(state).zoom
  );
  const isEligible = useCompareViewportStore(
    state => selectViewport(panelId)(state).isEligible
  );
  const isSynced = useCompareViewportStore(
    state => selectViewport(panelId)(state).isSynced
  );
  const controlRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const activeOwnerRef = useRef<string | null>(null);
  const isActive = useCompareZoomActivation({
    panelId,
    view,
    isSynced,
    controlRef,
    menuRef,
    activeOwnerRef,
  });
  const onControlZoom = useCallback(
    (nextZoom: Zoom) => {
      useCompareViewportStore.getState().setZoom(panelId, nextZoom);
    },
    [panelId]
  );

  if (!isEligible) {
    return null;
  }
  return (
    <div ref={controlRef}>
      {compareZoomEnabled && isActive && (
        <CompareZoomShortcuts
          panelId={panelId}
          activeOwnerRef={activeOwnerRef}
        />
      )}
      <ZoomControl
        source="compare"
        compact={view === 'workspace'}
        disabled={!isSynced}
        currentZoomLevel={zoom}
        zoomToFitPercentage={null}
        setCurrentZoomLevel={onControlZoom}
        onMenuOpenChange={onMenuOpenChange}
        menuRef={menuRef}
      />
    </div>
  );
}
